import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Footer from "@/components/Footer";
import { ARTICLES, fetchPublishedStories } from "@/data/journal";

export default function Article() {
  const { slug } = useParams();
  const [articles, setArticles] = useState(ARTICLES);

  useEffect(() => {
    fetchPublishedStories()
      .then((stories) => setArticles([...stories, ...ARTICLES]))
      .catch(() => {});
  }, []);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FBF9F5]">
        <PageHeader />
        <main className="px-6 pt-40 pb-24 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">Article not found.</p>
          <Link to="/journal" className="text-[#FF4E00] font-mono text-xs uppercase tracking-[0.2em] mt-4 inline-block">
            ← Back to Journal
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <PageHeader />
      <main className="pb-24 sm:pb-32">
        <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
          <img src={article.img} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF9F5] via-transparent to-transparent" />
        </div>
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="px-6 sm:px-12 lg:px-20 max-w-3xl mx-auto -mt-16 relative"
          data-testid="article-page"
        >
          <Link
            to="/journal"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#FF4E00] transition-colors mb-6"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Journal
          </Link>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> {article.author ? `By ${article.author} · ` : ""}{article.category} · {article.read} read{article.date ? ` · ${article.date}` : ""}
          </p>
          <h1 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-[#0D1117] tracking-tight leading-[1.12] mt-5">
            {article.title}
          </h1>
          <div className="mt-8 space-y-6">
            {article.body.map((p, i) => (
              <p key={i} className={`text-[#374151] leading-relaxed ${i === 0 ? "text-base md:text-lg" : "text-sm sm:text-base"}`}>
                {p}
              </p>
            ))}
          </div>
          <div className="mt-12 rounded-3xl bg-[#F4F2EC] p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <p className="font-serif text-lg text-[#0D1117]">Put it into practice with the tribe.</p>
            <Link
              to="/challenges"
              data-testid="article-cta"
              className="shrink-0 inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-colors duration-300"
            >
              Join a Challenge
            </Link>
          </div>
        </motion.article>
      </main>
      <Footer />
    </div>
  );
}
